
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { LayoutDashboard, Trash2, AlertTriangle, X, List, Rows, Search } from 'lucide-react';
import { Order, Driver, SystemConfig } from '../../types';
import { OrderList } from '../OrderList';
import { formatRouteTag, getFleetLabel } from '../../services/logistics/core/routePresentation';

interface Props {
  pendingOrders: Order[];
  focusedOrderId: string | null;
  onToggleFocus: (id: string | null) => void;
  onDeleteOrder: (id: string) => void;
  onEditOrder: (order: Order) => void;
  drivers: Driver[];
  onAssignDriver: (orderId: string, driverId: string) => void;
  config: SystemConfig;
  isSimpleView: boolean;
  onDispatchCluster: (orders: Order[], mode: 'own' | 'third', driverId?: string) => void;
  onToggleReady: (orderId: string) => void;
  onNotifyDelay: (orderId: string) => void;
  onClearQueue: () => void;
  handleDeleteMultipleOrders?: (ids: string[]) => void;
  viewMode: 'triage' | 'map' | 'analytics';
  cardViewMode: 'standard' | 'compact';
  onToggleCardView: (mode: 'standard' | 'compact') => void;
  onConfirmOrder: (orderId: string) => void;
  onTrackOrder: (orderId: string) => void;
  onManualComplete: (orderId: string) => void;
  suggestion?: any;
}

export const TriageSidebar: React.FC<Props> = ({
  pendingOrders, focusedOrderId, onToggleFocus, onDeleteOrder, onEditOrder, drivers, onAssignDriver, config, isSimpleView, onDispatchCluster, onToggleReady, onNotifyDelay, onClearQueue, handleDeleteMultipleOrders, viewMode, cardViewMode, onToggleCardView, onConfirmOrder, onTrackOrder, onManualComplete, suggestion
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isConfirmingClear, setIsConfirmingClear] = useState(false);
  const [timeTick, setTimeTick] = useState(() => Math.floor(Date.now() / 60000));

  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setTimeTick(Math.floor(Date.now() / 60000));
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  const handleToggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return pendingOrders;
    const lower = searchTerm.toLowerCase();
    return pendingOrders.filter(o =>
      (o.displayId || o.id).toString().toLowerCase().includes(lower) ||
      o.customerName?.toLowerCase().includes(lower) ||
      o.neighborhood?.toLowerCase().includes(lower)
    );
  }, [pendingOrders, searchTerm]);

  // v33.0: Separação Logística vs Balcão
  const deliveryOrders = useMemo(() => filteredOrders.filter(o => o.type === 'DELIVERY'), [filteredOrders]);
  const takeoutOrders = useMemo(() => filteredOrders.filter(o => o.type === 'TAKEOUT'), [filteredOrders]);

  // Agrupamento visual guiado por routes + smartClusterId (fonte unica)
  const processedDeliveryOrders = useMemo(() => {
    const routeMetaByOrder = new Map<string, {
      groupId: string;
      groupIndex: number;
      totalInGroup: number;
      routeMode: 'own' | 'third';
      routeAssignee: string;
    }>();

    (suggestion?.routes || []).forEach((route: any, routeIdx: number) => {
      const groupId = route.orders?.[0]?.smartClusterId || `cluster_${routeIdx}`;
      (route.orders || []).forEach((order: any, orderIdx: number) => {
        const orderId = typeof order === 'string' ? order : order.id;
        if (!orderId) return;
        routeMetaByOrder.set(orderId, {
          groupId,
          groupIndex: orderIdx + 1,
          totalInGroup: route.orders.length,
          routeMode: route.type,
          routeAssignee: route.assigneeName || (route.type === 'own' ? 'Sem motoboy' : 'App parceiro')
        });
      });
    });

    const fallbackCounts = new Map<string, number>();
    deliveryOrders.forEach(order => {
      if (order.smartClusterId) {
        fallbackCounts.set(order.smartClusterId, (fallbackCounts.get(order.smartClusterId) || 0) + 1);
      }
    });

    const getFallbackMode = (order: Order): 'own' | 'third' => {
      if (order.status === 'dispatched_3rd_party' || order.optimizationOutcome === 'third' || order.manualAssignment === 'third') return 'third';
      return 'own';
    };

    return deliveryOrders.map(order => {
      const routeMeta = routeMetaByOrder.get(order.id);
      const fallbackGroupId = order.smartClusterId || null;
      const groupId = routeMeta?.groupId || fallbackGroupId;
      const routeMode = routeMeta?.routeMode || getFallbackMode(order);
      const routeAssignee = routeMeta?.routeAssignee
        || (routeMode === 'third' ? (order.bestProvider || 'App parceiro') : (drivers.find(d => d.id === order.driverId)?.name || 'Sem motoboy'));

      return {
        ...order,
        groupId,
        groupIndex: routeMeta?.groupIndex || order.routeSequence || (groupId ? 1 : 0),
        totalInGroup: routeMeta?.totalInGroup || (groupId ? (fallbackCounts.get(groupId) || 1) : 1),
        groupColor: routeMode === 'third' ? '#f97316' : '#6366f1',
        routeMode,
        routeAssignee,
        routeTag: formatRouteTag(groupId)
      };
    });
  }, [deliveryOrders, suggestion, drivers]);

  return (
    <div className={`flex flex-col bg-app-bg border-r border-border-dim w-full md:w-[320px] xl:w-[360px] 2xl:w-[420px] shrink-0 z-20 ${viewMode !== 'triage' ? 'hidden md:flex' : 'flex'}`}>
      <div className="h-12 px-2 xl:px-4 flex items-center justify-between border-b border-border-dim">
        <div className="flex items-center gap-2 font-bold text-text-primary text-sm uppercase-ops">
          <LayoutDashboard className="w-4 h-4 text-sal-cyber-teal" /> 
          <span className="hidden md:inline">TRIAGEM</span>
          <span className="text-text-muted">({pendingOrders.length})</span>
        </div>
        <div className="flex items-center gap-2 xl:gap-3 flex-1 px-2 xl:px-4 min-w-0">
          <div className="relative group flex-1 max-w-[140px] xl:max-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-icon-muted group-hover:text-sal-cyber-teal transition-colors" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-black/5 dark:bg-white/[0.02] hover:bg-black/[0.08] dark:hover:bg-white/[0.05] focus:bg-black/[0.08] dark:focus:bg-white/[0.05] border border-border-dim focus:border-sal-cyber-teal/30 rounded-lg pl-8 pr-3 py-1 text-xs text-text-primary placeholder:text-text-muted/50 focus:outline-none transition-all"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-icon-muted hover:text-text-primary"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => { if (isConfirmingClear) { onClearQueue(); setIsConfirmingClear(false); } else { setIsConfirmingClear(true); setTimeout(() => setIsConfirmingClear(false), 3000); } }} className={`p-1.5 rounded-lg transition-all ${isConfirmingClear ? 'bg-status-critical text-badge-text-inv' : 'text-text-muted hover:text-status-critical'}`}>
              {isConfirmingClear ? <AlertTriangle className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
            </button>
            <div className="flex bg-card-bg/50 p-0.5 rounded-lg border border-white/5">
              <button type="button" onClick={() => onToggleCardView('standard')} className={`p-1 rounded transition-all ${cardViewMode === 'standard' ? 'bg-sal-cyber-teal text-badge-text-inv' : 'text-text-muted'}`}><Rows className="w-3 h-3" /></button>
              <button type="button" onClick={() => onToggleCardView('compact')} className={`p-1 rounded transition-all ${cardViewMode === 'compact' ? 'bg-sal-cyber-teal text-badge-text-inv' : 'text-text-muted'}`}><List className="w-3 h-3" /></button>
            </div>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="flex-1 overflow-hidden no-scrollbar">
        {containerHeight > 0 && (
          <OrderList
            deliveryOrders={processedDeliveryOrders}
            takeoutOrders={takeoutOrders}
            focusedOrderId={focusedOrderId}
            onToggleFocus={onToggleFocus}
            onDeleteOrder={onDeleteOrder}
            onEditOrder={onEditOrder}
            drivers={drivers}
            onAssignDriver={onAssignDriver}
            config={config}
            isSimpleView={isSimpleView}
            onDispatchCluster={onDispatchCluster}
            onToggleReady={onToggleReady}
            onNotifyDelay={onNotifyDelay}
            onConfirmOrder={onConfirmOrder}
            onTrackOrder={onTrackOrder}
            onManualComplete={onManualComplete}
            cardViewMode={cardViewMode}
            selectedIds={selectedIds}
            onToggleSelection={handleToggleSelection}
            suggestion={suggestion}
            timeTick={timeTick}
            height={containerHeight}
          />
        )}
      </div>
    </div>
  );
};
